import classNames from 'classnames';
import { useContext, type CSSProperties, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import TeamAvatar from 'app/components/TeamAvatar';
import { formatDate } from 'app/utils/date';
import ConfigContext from 'app/common/ConfigContext';
import liaisonsStyle from 'app/components/Blocks/BracketsBlock/BracketWithSeeds/style.module.scss';
import type { BracketLiaison } from 'app/components/Blocks/BracketsBlock/types';
import FloatingModal from 'app/components/FloatingModal';
import BracketMatchRecap from 'app/components/BracketMatchRecap';
import CheckIcon from 'app/icons/check.svg';
import { getMatchOpponents } from 'app/components/Blocks/BracketsBlock/utils';
import { isDefined } from 'app/utils/types';

import style from './style.module.scss';

interface Props {
  match: TournamentBracketMatch;
  prevLiaison?: BracketLiaison;
  nextLiaison?: BracketLiaison;
  nextSeedLength?: number;
  nextLiaisonIsTop?: boolean;
  videogame: VideogameSlug;
  videogameTitle: CounterStrikeVersion | null;
  tabIndex?: number;
  display: 'default' | 'compact' | 'compact_without_liaisons';
  selectedTeamId?: number | null;
  hoveredTeamId?: number | null;
  onOpponentHover?: (teamId: number | null) => void;
}

const BracketMatch: FC<Props> = ({
  match,
  prevLiaison,
  nextLiaison,
  nextSeedLength,
  nextLiaisonIsTop,
  videogame,
  tabIndex,
  videogameTitle,
  display,
  selectedTeamId,
  hoveredTeamId,
  onOpponentHover
}) => {
  const { t } = useTranslation();
  const { lang } = useContext(ConfigContext);

  const isLive = match.status === 'running';
  const dateFormat: Intl.DateTimeFormatOptions =
    display === 'compact_without_liaisons'
      ? { day: '2-digit', month: '2-digit' }
      : { month: 'long', day: 'numeric' };
  const date = match.begin_at && formatDate(new Date(match.begin_at), lang, dateFormat);

  const hasScore = match.status === 'finished' || match.status === 'running';

  const matchOpponents = getMatchOpponents(match);
  const hasMatchRecap = matchOpponents.some(opponent => opponent.slug !== 'tbd');

  return (
    <div className={style.match}>
      {display !== 'compact_without_liaisons' && (
        <div
          className={classNames(
            liaisonsStyle.prevLiaisonContainer,
            prevLiaison && liaisonsStyle[prevLiaison]
          )}
        >
          <div />
        </div>
      )}
      <FloatingModal
        content={
          <BracketMatchRecap match={match} videogame={videogame} videogameTitle={videogameTitle} />
        }
        openedClassName={style.matchRecapOpened}
        className={classNames(style.matchContent, {
          [style.liveMatch]: isLive
        })}
        tabIndex={tabIndex}
        withPortal
        enabled={hasMatchRecap}
      >
        <>
          {display !== 'compact_without_liaisons' && (
            <>
              {isLive ? (
                <div className={style.liveIndicatorContainer}>
                  <div className={style.liveIndicator}>{t('component_live-tag_live')}</div>
                </div>
              ) : (
                <>{display === 'default' && <div className={style.date}>{date}</div>}</>
              )}
            </>
          )}

          <div
            className={classNames(style.opponents, {
              [style.withMatchRecap]: hasMatchRecap,
              [style.compact]: display !== 'default',
              [style.compactWithoutLiaisons]: display === 'compact_without_liaisons'
            })}
          >
            {display === 'compact_without_liaisons' && (
              <>
                {isLive ? (
                  <div className={style.smallLiveIndicatorContainer}>
                    <div className={style.smallLiveIndicator}>{t('component_live-tag_live')}</div>
                  </div>
                ) : (
                  <div className={style.smallDate}>{date}</div>
                )}
              </>
            )}
            {matchOpponents
              .filter(opponent => opponent.id !== selectedTeamId)
              .map(opponent => {
                const hasSelectedTeamResult =
                  display === 'compact_without_liaisons' &&
                  selectedTeamId &&
                  match.status === 'finished';
                const isSelectedTeamWinner =
                  hasSelectedTeamResult && match.winner_id && match.winner_id === selectedTeamId;
                const thisOpponent = matchOpponents.find(opp => opp.id === opponent.id);
                const otherOpponent = matchOpponents.find(opp => opp.id !== opponent.id);

                const winner = !!match.winner_id && match.winner_id === opponent.id;
                const loser = !!match.winner_id && match.winner_id !== opponent.id;
                const tie = hasSelectedTeamResult && match.winner_id === null;

                const forfeit = match.forfeit && loser;
                const gameAdvantage = match.game_advantage && match.game_advantage === opponent.id;
                const { score } = opponent;

                const usedScore = hasScore ? score : '';

                const isTbd = opponent.slug === 'tbd';

                return (
                  <div
                    key={opponent.id}
                    className={classNames(style.opponent, {
                      [style.matchWinner]: winner,
                      [style.matchLoser]: loser,
                      [style.opponentHovered]: !isTbd && opponent.id === hoveredTeamId
                    })}
                    onMouseEnter={!isTbd ? () => onOpponentHover?.(opponent.id) : undefined}
                    onMouseLeave={!isTbd ? () => onOpponentHover?.(null) : undefined}
                  >
                    {hasSelectedTeamResult &&
                      hasScore &&
                      isDefined(thisOpponent?.score) &&
                      isDefined(otherOpponent?.score) && (
                        <div className={style.scoresContainer}>
                          <span
                            className={classNames({
                              [style.loser]: otherOpponent.score < thisOpponent.score
                            })}
                          >
                            {otherOpponent.score}
                          </span>
                          <span>-</span>
                          <span
                            className={classNames({
                              [style.loser]: thisOpponent.score < otherOpponent.score
                            })}
                          >
                            {thisOpponent.score}
                          </span>
                        </div>
                      )}
                    <div className={style.opponentIdentification}>
                      {!isTbd && <TeamAvatar team={opponent} />}
                      <span
                        className={classNames(style.opponentName, { [style.tbdOppponent]: isTbd })}
                      >
                        <span className={style.nameLarge}>{opponent.name}</span>
                        <span className={style.nameSmall}>
                          {opponent.acronym ?? opponent.name.slice(0, 3)}
                        </span>
                      </span>
                    </div>
                    <div className={style.matchInfoContainer}>
                      {match.forfeit && winner && <CheckIcon className={style.winnerIcon} />}
                      {forfeit && (
                        <span className={classNames(style.matchInfoTag, style.forfeitTagLarge)}>
                          {t('blocks_bracket_match_forfeit-long')}
                        </span>
                      )}
                      {forfeit && (
                        <span className={classNames(style.matchInfoTag, style.forfeitTagSmall)}>
                          {t('blocks_bracket_match_forfeit-short')}
                        </span>
                      )}
                      {gameAdvantage && (
                        <span className={style.matchInfoTag}>
                          {t('blocks_bracket_match_game-advantage')}
                        </span>
                      )}
                      {!isDefined(selectedTeamId) && (display === 'default' || !match.forfeit) && (
                        <span className={style.score}>{usedScore}</span>
                      )}
                      {hasSelectedTeamResult && (
                        <div
                          className={classNames(style.resultIndicator, {
                            [style.resultIndicatorWinner]: !!isSelectedTeamWinner && !tie,
                            [style.resultIndicatorTie]: tie
                          })}
                        />
                      )}
                    </div>
                  </div>
                );
              })
              .reduce((acc, curr) => (
                <>
                  {acc}
                  {display !== 'default' ? (
                    <div className={style.opponentsSeparator}>
                      {hasScore ? '-' : t('all-versus')}
                    </div>
                  ) : null}
                  {curr}
                </>
              ))}
          </div>
        </>
      </FloatingModal>
      {display !== 'compact_without_liaisons' && (
        <div
          className={classNames(
            liaisonsStyle.nextLiaisonContainer,
            nextLiaison && liaisonsStyle[nextLiaison]
          )}
          style={
            nextSeedLength !== undefined
              ? ({
                  '--next-seed-length': nextSeedLength,
                  '--next-liaison-is-top': nextLiaisonIsTop ? 1 : 0
                } as CSSProperties)
              : undefined
          }
        >
          <div />
        </div>
      )}
    </div>
  );
};

BracketMatch.displayName = 'BracketMatch';

export default BracketMatch;
